/**
 * Typed query + mutation helpers over Dexie.
 *
 * Reads return Promises (use with `useLiveQuery(() => getComenzi())`).
 * Every mutation writes through Dexie so subscribed screens re-render.
 *
 * Feature agents: this is a stable API. If you need a query that isn't here,
 * add a small LOCAL helper inside your own screen file — do NOT edit this file.
 */

import { azi, isoZi, statusUrmator } from '@/lib/format'
import { type ParseResult, parseOrder } from '@/lib/parseOrder'
import {
  type Activitate,
  type Client,
  type Comanda,
  type ComandaLinie,
  type ComandaStatus,
  type Factura,
  type Masina,
  type Mesaj,
  type Produs,
  type Setari,
  type Sofer,
  db,
} from './dexie'
import { COMPANIE } from './seed'

const round2 = (n: number) => Math.round(n * 100) / 100
const round1 = (n: number) => Math.round(n * 10) / 10
const nowIso = () => new Date().toISOString()

// --- clienți ---------------------------------------------------------------
export const getClienti = () => db.clienti.orderBy('nume').toArray()
export const getClient = (id: string) => db.clienti.get(id)

// --- produse ---------------------------------------------------------------
export const getProduse = () => db.produse.orderBy('cod').toArray()
export const getProdus = (id: string) => db.produse.get(id)
export const getProdusByCod = (cod: string) => db.produse.where('cod').equals(cod).first()
export const getProduseLowStock = (prag = 80) => db.produse.filter((p) => p.stocKg < prag).toArray()

export async function setPretProdus(id: string, pretKg: number): Promise<void> {
  await db.produse.update(id, { pretKg: round2(pretKg) })
}

export async function setStocProdus(id: string, stocKg: number): Promise<void> {
  await db.produse.update(id, { stocKg: round1(stocKg) })
}

// --- comenzi ---------------------------------------------------------------
export function getComenzi(status?: ComandaStatus): Promise<Comanda[]> {
  if (status) return db.comenzi.where('status').equals(status).toArray()
  return db.comenzi.toArray()
}
export const getComanda = (id: string) => db.comenzi.get(id)
export const getComenziByClient = (clientId: string) =>
  db.comenzi.where('clientId').equals(clientId).reverse().sortBy('dataComanda')

export function totalsFromLinii(linii: ComandaLinie[]): { totalKg: number; totalRON: number } {
  return {
    totalKg: round1(linii.reduce((s, l) => s + l.cantitateKg, 0)),
    totalRON: round2(linii.reduce((s, l) => s + l.cantitateKg * l.pretKg, 0)),
  }
}

/** Move a comandă to the next funnel status and append the timeline. */
export async function advanceComanda(id: string): Promise<Comanda | undefined> {
  const cmd = await db.comenzi.get(id)
  if (!cmd) return undefined
  const next = statusUrmator(cmd.status)
  if (!next) return cmd

  const timeline = [...cmd.timeline, { status: next, ora: nowIso() }]
  await db.comenzi.update(id, { status: next, timeline })

  // dispatch side-effects on the fleet
  if (next === 'Pe drum' && cmd.masinaId) {
    await db.masini.update(cmd.masinaId, { status: 'pe traseu' })
    if (cmd.soferId) await db.soferi.update(cmd.soferId, { status: 'pe traseu' })
  }

  const client = await db.clienti.get(cmd.clientId)
  await addActivitate({
    tip: 'comanda',
    icon: next === 'Livrat' ? 'PackageCheck' : 'ArrowRightCircle',
    text: `Comandă ${next.toLowerCase()} — ${client?.nume ?? cmd.clientId}`,
  })

  return db.comenzi.get(id)
}

/** Overwrite the lines of a comandă and recompute totals. */
export async function setComandaLinii(id: string, linii: ComandaLinie[]): Promise<void> {
  await db.comenzi.update(id, { linii, ...totalsFromLinii(linii) })
}

/** Assign a driver + van; marks both as "pe traseu". */
export async function setSoferMasina(
  comandaId: string,
  soferId: string,
  masinaId: string,
): Promise<void> {
  await db.comenzi.update(comandaId, { soferId, masinaId })
  await db.soferi.update(soferId, { status: 'pe traseu' })
  await db.masini.update(masinaId, { status: 'pe traseu' })
}

// --- șoferi & mașini -------------------------------------------------------
export const getSoferi = () => db.soferi.toArray()
export const getSofer = (id: string) => db.soferi.get(id)
export const getMasini = () => db.masini.toArray()
export const getMasina = (id: string) => db.masini.get(id)

// --- mesaje ----------------------------------------------------------------
export function getMesaje(status?: Mesaj['status']): Promise<Mesaj[]> {
  const p = status ? db.mesaje.where('status').equals(status).toArray() : db.mesaje.toArray()
  return p.then((rows) => rows.sort((a, b) => (a.ora < b.ora ? 1 : -1)))
}
export const getMesaj = (id: string) => db.mesaje.get(id)

export interface MesajParse extends ParseResult {
  mesaj: Mesaj
  client?: Client
  /** the client's usual order, as ready-to-use lines (recurrent-client pre-fill) */
  uzualaLinii: ComandaLinie[]
}

/** Read-only preview: parse a message + surface the recurrent client's usual order. */
export async function parseMesaj(mesajId: string): Promise<MesajParse | undefined> {
  const mesaj = await db.mesaje.get(mesajId)
  if (!mesaj) return undefined
  const client = mesaj.clientId ? await db.clienti.get(mesaj.clientId) : undefined
  const produse = await getProduse()
  const parsed = parseOrder(mesaj.text, produse, { pretSpecial: client?.pretSpecial })
  const uzualaLinii = await uzualaToLinii(client, produse)
  return { mesaj, client, uzualaLinii, ...parsed }
}

async function uzualaToLinii(
  client: Client | undefined,
  produse: Produs[],
): Promise<ComandaLinie[]> {
  if (!client) return []
  const byId = new Map(produse.map((p) => [p.id, p]))
  return client.comandaUzuala.flatMap((u) => {
    const p = byId.get(u.produsId)
    if (!p) return []
    return [
      {
        produsId: p.id,
        cod: p.cod,
        nume: p.nume,
        cantitateKg: u.cantitateKg,
        pretKg: client.pretSpecial[p.id] ?? p.pretKg,
        um: p.um,
      },
    ]
  })
}

async function nextComandaId(): Promise<string> {
  const ids = await db.comenzi.toCollection().primaryKeys()
  const max = ids.reduce((m, id) => {
    const n = Number(String(id).replace(/\D/g, ''))
    return Number.isFinite(n) && n > m ? n : m
  }, 1020)
  return `cmd-${max + 1}`
}

/**
 * Turn a message into a Nou comandă. Uses `linii` if given, otherwise the
 * parsed lines, falling back to the recurrent client's usual order.
 * Marks the message "transformat" and links it to the new order.
 */
export async function transformMesajInComanda(
  mesajId: string,
  linii?: ComandaLinie[],
): Promise<string | undefined> {
  const preview = await parseMesaj(mesajId)
  if (!preview) return undefined
  const { mesaj, client } = preview

  let liniiFinale: ComandaLinie[] =
    linii ??
    preview.linii.map((l) => ({
      produsId: l.produsId,
      cod: l.cod,
      nume: l.nume,
      cantitateKg: l.cantitateKg,
      pretKg: l.pretKg,
      um: l.um,
    }))
  if (liniiFinale.length === 0) liniiFinale = preview.uzualaLinii

  const id = await nextComandaId()
  const comanda: Comanda = {
    id,
    clientId: client?.id ?? '',
    dataComanda: nowIso(),
    dataLivrare: isoZi(new Date(azi().getTime() + 86_400_000)),
    status: 'Nou',
    linii: liniiFinale,
    ...totalsFromLinii(liniiFinale),
    adresaLivrare: client?.adresa ?? '',
    mesajOriginal: mesaj.text,
    timeline: [{ status: 'Nou', ora: nowIso() }],
  }

  await db.comenzi.put(comanda)
  await db.mesaje.update(mesajId, { status: 'transformat', comandaId: id })
  await addActivitate({
    tip: 'comanda',
    icon: 'MessageSquarePlus',
    text: `Comandă nouă din mesaj — ${client?.nume ?? mesaj.numeAfisat}`,
  })
  return id
}

// --- facturi ---------------------------------------------------------------
export const getFacturi = () =>
  db.facturi.toArray().then((rows) => rows.sort((a, b) => b.numar - a.numar))
export const getFactura = (id: string) => db.facturi.get(id)
export const getFacturaByComanda = (comandaId: string) =>
  db.facturi.where('comandaId').equals(comandaId).first()

async function nextFacturaNumar(): Promise<number> {
  const rows = await db.facturi.toArray()
  return rows.reduce((m, f) => Math.max(m, f.numar), 1046) + 1
}

/**
 * Generate an invoice for a delivered order (TVA 9% aliment), link it, and
 * advance the order to Facturat. Idempotent: returns the existing invoice if any.
 */
export async function genereazaFactura(comandaId: string): Promise<string | undefined> {
  const cmd = await db.comenzi.get(comandaId)
  if (!cmd) return undefined
  if (cmd.facturaId) return cmd.facturaId

  const client = await db.clienti.get(cmd.clientId)
  const numar = await nextFacturaNumar()
  const id = `f-${numar}`
  const subtotal = round2(cmd.totalRON)
  const totalTVA = round2(subtotal * 0.09)

  const factura: Factura = {
    id,
    comandaId: cmd.id,
    serie: 'EXS',
    numar,
    dataEmitere: isoZi(azi()),
    furnizor: { nume: COMPANIE.companie, cui: COMPANIE.cui, adresa: COMPANIE.adresa },
    client: {
      nume: client?.nume ?? '',
      cui: client ? `RO ${18_000_000 + Number(client.id.replace(/\D/g, ''))}` : '',
      adresa: client?.adresa ?? '',
    },
    linii: cmd.linii.map((l) => ({
      nume: l.nume ?? '',
      cantitateKg: l.cantitateKg,
      pretKg: l.pretKg,
      um: l.um,
    })),
    tva: 0.09,
    subtotal,
    totalTVA,
    total: round2(subtotal + totalTVA),
    eFacturaStatus: 'validat',
  }

  await db.facturi.put(factura)
  const timeline =
    cmd.status === 'Facturat'
      ? cmd.timeline
      : [...cmd.timeline, { status: 'Facturat' as ComandaStatus, ora: nowIso() }]
  await db.comenzi.update(comandaId, { facturaId: id, status: 'Facturat', timeline })
  await addActivitate({
    tip: 'factura',
    icon: 'FileText',
    text: `Factură trimisă la ANAF e-Factura — EXS ${numar}`,
  })
  return id
}

// --- activitate ------------------------------------------------------------
export const getActivitate = (limit = 12) =>
  db.activitate
    .toArray()
    .then((rows) => rows.sort((a, b) => (a.ora < b.ora ? 1 : -1)).slice(0, limit))

export async function addActivitate(item: {
  tip: string
  text: string
  icon?: string
  ora?: string
}): Promise<void> {
  await db.activitate.put({
    id: `act-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`,
    tip: item.tip,
    text: item.text,
    icon: item.icon,
    ora: item.ora ?? nowIso(),
  })
}

// --- setări ----------------------------------------------------------------
export const getSetari = (): Promise<Setari | undefined> => db.setari.get(1)

// --- KPI & aggregates ------------------------------------------------------
export interface KpiAzi {
  comenziAzi: number
  kgLivrateAzi: number
  venitAzi: number
  inAsteptare: number
  masiniPeTraseu: number
}

export async function kpiAzi(): Promise<KpiAzi> {
  const today = isoZi(azi())
  const [comenzi, masini] = await Promise.all([db.comenzi.toArray(), db.masini.toArray()])
  const azi_ = comenzi.filter((c) => c.dataLivrare === today)
  const livrate = comenzi.filter(
    (c) => c.dataLivrare === today && (c.status === 'Livrat' || c.status === 'Facturat'),
  )
  const inAsteptare = comenzi.filter(
    (c) => c.status === 'Nou' || c.status === 'Confirmat' || c.status === 'În pregătire',
  )
  return {
    comenziAzi: azi_.length,
    kgLivrateAzi: round1(livrate.reduce((s, c) => s + c.totalKg, 0)),
    venitAzi: round2(livrate.reduce((s, c) => s + c.totalRON, 0)),
    inAsteptare: inAsteptare.length,
    masiniPeTraseu: masini.filter((m) => m.status === 'pe traseu').length,
  }
}

/** Today's delivery progress — livrate vs. total planned for today. */
export async function progresLivrareAzi(): Promise<{ livrate: number; total: number }> {
  const today = isoZi(azi())
  const comenzi = await db.comenzi.where('dataLivrare').equals(today).toArray()
  return {
    total: comenzi.length,
    livrate: comenzi.filter((c) => c.status === 'Livrat' || c.status === 'Facturat').length,
  }
}

const VANZARE_STATUSES: ComandaStatus[] = ['Livrat', 'Facturat']

export interface AgregatProdus {
  produsId: string
  cod: string
  nume: string
  kg: number
  ron: number
}

/** Top products by kg across delivered/invoiced orders. */
export async function topProduse(limit = 6): Promise<AgregatProdus[]> {
  const comenzi = await db.comenzi.toArray()
  const acc = new Map<string, AgregatProdus>()
  for (const c of comenzi) {
    if (!VANZARE_STATUSES.includes(c.status)) continue
    for (const l of c.linii) {
      const cur = acc.get(l.produsId) ?? {
        produsId: l.produsId,
        cod: l.cod ?? l.produsId,
        nume: l.nume ?? l.produsId,
        kg: 0,
        ron: 0,
      }
      cur.kg = round1(cur.kg + l.cantitateKg)
      cur.ron = round2(cur.ron + l.cantitateKg * l.pretKg)
      acc.set(l.produsId, cur)
    }
  }
  return [...acc.values()].sort((a, b) => b.kg - a.kg).slice(0, limit)
}

export interface AgregatClient {
  clientId: string
  nume: string
  ron: number
  comenzi: number
}

/** Sales grouped by client (delivered/invoiced). */
export async function vanzariPeClient(limit = 8): Promise<AgregatClient[]> {
  const [comenzi, clienti] = await Promise.all([db.comenzi.toArray(), db.clienti.toArray()])
  const nume = new Map(clienti.map((c) => [c.id, c.nume]))
  const acc = new Map<string, AgregatClient>()
  for (const c of comenzi) {
    if (!VANZARE_STATUSES.includes(c.status)) continue
    const cur = acc.get(c.clientId) ?? {
      clientId: c.clientId,
      nume: nume.get(c.clientId) ?? c.clientId,
      ron: 0,
      comenzi: 0,
    }
    cur.ron = round2(cur.ron + c.totalRON)
    cur.comenzi += 1
    acc.set(c.clientId, cur)
  }
  return [...acc.values()].sort((a, b) => b.ron - a.ron).slice(0, limit)
}

export interface PunctZi {
  zi: string
  label: string
  ron: number
  kg: number
}

/** Revenue + kg per day for the last `zile` days (oldest → newest). */
export async function vanzariPeZi(zile = 7): Promise<PunctZi[]> {
  const comenzi = await db.comenzi.toArray()
  const zileScurt = ['dum', 'lun', 'mar', 'mie', 'joi', 'vin', 'sâm']
  const out: PunctZi[] = []
  for (let i = zile - 1; i >= 0; i--) {
    const d = new Date(azi().getTime() - i * 86_400_000)
    const key = isoZi(d)
    const dayOrders = comenzi.filter(
      (c) => c.dataLivrare === key && VANZARE_STATUSES.includes(c.status),
    )
    out.push({
      zi: key,
      label: zileScurt[d.getDay()] ?? '',
      ron: round2(dayOrders.reduce((s, c) => s + c.totalRON, 0)),
      kg: round1(dayOrders.reduce((s, c) => s + c.totalKg, 0)),
    })
  }
  return out
}
